import { Component, OnInit, Output, EventEmitter, OnDestroy, NgZone } from '@angular/core';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AuthenticationService } from '../common/services/authentication.service';
import { environment } from '@environment';
import PlatformType = Office.PlatformType;
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';

@Component({
    selector: 'app-login-page',
    templateUrl: './login-page.component.html',
    styleUrls: ['./login-page.component.scss'],
    imports: [TranslateModule, LoaderComponent],
})
export class LoginPageComponent implements OnInit, OnDestroy {
    @Output() loginIn: EventEmitter<any> = new EventEmitter();

    dialog;
    isDialogWindowOpened: boolean;
    isLoading: boolean;
    isNeedWindowMessageListener: boolean;

    constructor(
        private authenticationService: AuthenticationService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private zone: NgZone,
    ) {}

    ngOnInit(): void {
        Office.onReady(() => {
            this.isNeedWindowMessageListener =
                [PlatformType.Mac, PlatformType.OfficeOnline].indexOf(Office.context.platform) > -1;

            if (this.isNeedWindowMessageListener) {
                this.initLoginAppCommunicator();
            }
        });
    }

    openIframe(isAzureLogin?): void {
        const iframeUrl = isAzureLogin
            ? environment.masterUrl + 'ExternalLogin?clientId=outlookAddin'
            : environment.loginAppUrl + '#/?isFromOutlook=true';

        Office.onReady(() => {
            Office.context.ui.displayDialogAsync(
                iframeUrl,
                { width: 60, height: 60, displayInIframe: !isAzureLogin },
                (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        console.log('failed', asyncResult.error.code, asyncResult.error.message);
                    } else {
                        // zone.run is needed here because code is run out of angular zone (in office js)
                        this.zone.run(() => {
                            this.isDialogWindowOpened = true;
                        });
                        this.dialog = asyncResult.value;
                        this.dialog.addEventHandler(
                            Office.EventType.DialogMessageReceived,
                            this.processLoginDialogMessage.bind(this),
                        );
                        this.dialog.addEventHandler(
                            Office.EventType.DialogEventReceived,
                            this.processLoginDialogEvent.bind(this),
                        );
                    }
                    console.log('asyncResult', asyncResult.status, asyncResult.value);
                },
            );
        });
    }

    processLoginDialogEvent(arg): void {
        switch (arg.error) {
            case 12006: // Dialog closed
                // zone.run is needed here because code is run out of angular zone (in office js)
                this.zone.run(() => {
                    this.isDialogWindowOpened = false;
                });
                break;
            default:
                // this.notificationsService.error(this.translateService.instant('Error!'), arg.error + this.translateService.instant(' error in dialog box'));
                break;
        }
    }

    processLoginDialogMessage(arg): void {
        const message = JSON.parse(arg.message);

        if (message && message.success) {
            this.authenticationService.updateAuthData(message.success);
            this.loginIn.emit();
        }

        if (message && message.failed) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Unable to authenticate user'),
            );
        }

        // zone.run is needed here because code is run out of angular zone (in office js)
        this.zone.run(() => {
            this.isDialogWindowOpened = false;
        });

        if (this.dialog) {
            this.dialog.close();
        }
    }

    private handleMessagesFromLogin(message): void {
        if (environment.loginAppUrl.indexOf(message.origin) === 0 && message.data) {
            console.log('handleMessagesFromLogin', message);

            if (message.data.success) {
                this.authenticationService.updateAuthData(JSON.parse(message.data.success));
                this.loginIn.emit();
            }

            // zone.run is needed here because code is run out of angular zone (in office js)
            this.zone.run(() => {
                this.isDialogWindowOpened = false;
            });

            if (this.dialog) {
                this.dialog.close();
            }
        }
    }

    private initLoginAppCommunicator(): void {
        window.addEventListener('message', this.handleMessagesFromLogin.bind(this));
    }

    ngOnDestroy(): void {
        if (this.isNeedWindowMessageListener) {
            window.removeEventListener('message', this.handleMessagesFromLogin.bind(this));
        }
    }
}
